/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Metrics from '@exc/metrics';

const buffer = new Set;
const metrics = new Metrics('exc.module-runtime.util');
let metricsTimeout: any;

function trackMetrics(path: string) {
  buffer.add(path.replace(/\[\d*\]$/, '[]'));
  clearTimeout(metricsTimeout);
  metricsTimeout = setTimeout(() => {
    metrics.event('Object.accessed', Array.from(buffer));
    buffer.clear();
  }, 500);
}

export function gettify(obj: Record<string, any>, path = ''): void {
  if (obj == null || typeof obj !== 'object') {
    return;
  }

  Object.entries(obj).forEach(([key, value], idx) => {
    let newPath = `${path}${path ? '.' : ''}${key}`;

    Array.isArray(value) ?
      value.forEach((o, i) => gettify(o, `${newPath}[${i}]`)) :
      gettify(value, newPath);

    Object.defineProperty(obj, key, {
      get: () => {
        trackMetrics(newPath);
        return value;
      }
    });
  });
}

export let isComponent = (skipCache = false): boolean => {
  const win = window as any;
  const url = new URL(win.location.href);
  const runtime = url.searchParams.get('_mr');
  const runtimeUrl = runtime || !win.EXC_US_HMR ?
    runtime :
    win.sessionStorage.getItem('unifiedShellMRScript');
  const isComp = decodeURIComponent(runtimeUrl || '').indexOf('component=1') > -1;

  // Skips caching the function so that it will run fresh next time.
  // NOTE: THIS SHOULD ONLY BE USED FOR TESTING.
  if (skipCache) {
    return isComp;
  }

  // Overriding the main function to only return the calculated value. This
  // ensures the calculation only happens once, as it's not possible for this
  // to change.
  isComponent = (): boolean => isComp;
  return isComponent();
};

export interface IframePerformance {
  done?: number;
  timeOrigin?: number;
  entries: PerformanceEntry[];
  timing: PerformanceTiming;
}

/*
Filters performance entries before sending to Shell.
 */
export function getPerformanceEntries(): IframePerformance|undefined {
  return {
    done: performance.now(),
    entries: performance.getEntries()
      .filter((e: PerformanceEntry) => (
        e.entryType === 'mark' || e.entryType === 'navigation' || e.entryType === 'paint' ||
        (e.entryType === 'resource' && /runtime.*\.js$/.test(e.name))
      )),
    timeOrigin: performance.timeOrigin,
    timing: performance.timing
  };
}

/**
 * Checks if current window is Unified Shell or iframe.
 * @returns true if running in Shell, false if running in an iframe
 */
export function isShell() {
  const w = (window as any);
  return (w.parent === w) || ('config' in w && w.config.tis === 1);
}
